'use es6';

import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import H4 from 'UIComponents/elements/headings/H4';
import UIPaginator from 'UIComponents/paginator/UIPaginator';
import { View } from 'foundations-components/components/View';
import { Section } from 'foundations-components/transitional/components/Section';
import { AppSortDropdown } from 'marketplace-ui-apps-core/results/components/AppSortDropdown';
import { SEARCH_RESULTS_MAX } from 'marketplace-ui-common/constants/search';
import { MediaQueryView } from 'marketplace-ui-common/layout/components/MediaQueryView';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import { isFailed, isFulfilled, isSucceeded } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import ResultsMaxWarning from 'marketplace-ui-common/storefront/components/ResultsMaxWarning';
import StorefrontResultsHeader from 'marketplace-ui-common/storefront/components/StorefrontResultsHeader';
import PropTypes from 'prop-types';
import { Fragment, useCallback, useMemo, useRef } from 'react';
import { DEFAULT_SEARCH_LENGTH } from '../../../constants';
import * as CustomPropTypes from '../../../constants/PropTypes';
import * as FilterKey from '../../../constants/search/FilterKey';
import SearchResult from '../../../records/SearchResult';
import AppCollectionsResultsSection from '../../../storefront/components/AppCollectionsResultsSection';
import { getFiltersHeadingI18nKey } from '../../../utils/i18n';
import { MobileFilterButton } from '../mobile/MobileFilterButton';
import PageOneLink from './PageOneLink';
import ResultsCount from './ResultsCount';
import ResultsFilters from './ResultsFilters';
import ResultsGrid from './ResultsGrid';
import ResultsHeaderTitle from './ResultsHeaderTitle';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getI18nKey = key => `appMarketplace.storefront.results.resultsPage.${key}`;
const resultPropsPropDefault = {};
const ResultsPage = ({
  activePage,
  description,
  emptyStateComponent: EmptyStateComponent,
  failedComponent: FailedComponent,
  filterable,
  filters,
  isAllCategoriesHomepage,
  offset = 0,
  onClearFilters,
  onFilterChange,
  onPageChange,
  onResultClick,
  onSortChange,
  referrer,
  requestState,
  resultProps = resultPropsPropDefault,
  resultsPerPage = DEFAULT_SEARCH_LENGTH,
  searchResult,
  sortable = true,
  title,
  trackInteraction
}) => {
  const {
    [FilterKey.CATEGORY_V2]: categoryV2,
    [FilterKey.SEARCH]: searchValue
  } = filters;
  const resultsRef = useRef(null);
  const {
    items: searchResults,
    total: searchResultsTotal
  } = searchResult;
  const searchResultsLength = searchResults.size;
  const hasResults = !!searchResultsTotal;
  const handlePageChange = useCallback(({
    target: {
      value
    }
  }) => {
    onPageChange(value, filters);
  }, [filters, onPageChange]);
  const [resultsLength, resultsTotal, resultsPageCount] = useMemo(() => {
    const total = Math.min(searchResultsTotal, SEARCH_RESULTS_MAX);
    return [searchResultsLength, total, Math.ceil(total / resultsPerPage)];
  }, [resultsPerPage, searchResultsLength, searchResultsTotal]);
  const filtersHeadingI18nKey = useMemo(() => getFiltersHeadingI18nKey(filters), [filters]);
  const renderedResultsHeaderTitle = title || /*#__PURE__*/_jsx(ResultsHeaderTitle, {
    categoryV2: categoryV2.toArray(),
    searchValue: searchValue
  });
  const renderedResultsCount = isFulfilled(requestState) ? /*#__PURE__*/_jsx(ResultsCount, {
    activePage: activePage,
    filtersHeadingI18nKey: filtersHeadingI18nKey,
    hasResults: hasResults,
    offset: offset,
    resultsLength: resultsLength,
    resultsTotal: resultsTotal,
    searchValue: searchValue
  }) : null;
  const validFilterTagKeys = filters.getValidFilterTagKeys();
  const renderedDefaultFilterBar = !validFilterTagKeys.isEmpty() ? /*#__PURE__*/_jsx(ResultsFilters, {
    filters: filters,
    onClearFilters: onClearFilters,
    onFilterChange: onFilterChange,
    trackInteraction: trackInteraction,
    validFilterTagKeys: validFilterTagKeys
  }) : null;
  const renderedResultsFilters = filterable ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(MediaQueryView, {
      min: ScreenSizes.MD,
      children: renderedDefaultFilterBar
    }), /*#__PURE__*/_jsx(MediaQueryView, {
      max: ScreenSizes.MD,
      children: /*#__PURE__*/_jsx(ResultsFilters, {
        filters: filters,
        isMobile: true,
        onClearFilters: onClearFilters,
        onFilterChange: onFilterChange,
        onSortChange: onSortChange,
        trackInteraction: trackInteraction,
        validFilterTagKeys: validFilterTagKeys,
        startSlot: /*#__PURE__*/_jsx(MobileFilterButton, {})
      })
    })]
  }) : null;
  const computedSortable = useMemo(() => {
    if (!sortable || !hasResults) {
      return false;
    }
    return true;
  }, [hasResults, sortable]);
  const renderedSortDropdown = computedSortable ? /*#__PURE__*/_jsx(AppSortDropdown, {
    filters: filters,
    onSortChange: (field, order) => {
      onSortChange(field, order);
      trackInteraction('change sort', {
        sortField: field,
        sortOrder: order
      }, 'storefront-change-sort');
    },
    onShowPreferredLanguageFirstChange: checked => {
      onFilterChange('showPreferredLanguageFirst', checked);
    }
  }) : null;
  const renderResultsGrid = useCallback(searchResultsArray => /*#__PURE__*/_jsx(ResultsGrid, {
    onResultClick: (result, index) => onResultClick(result, index, searchResultsLength),
    referrer: referrer,
    requestState: requestState,
    resultProps: resultProps,
    results: searchResultsArray
  }), [onResultClick, referrer, requestState, resultProps, searchResultsLength]);
  let renderedPageContent;
  if (isFailed(requestState) && FailedComponent) {
    renderedPageContent = /*#__PURE__*/_jsx(FailedComponent, {});
  } else if (isSucceeded(requestState) && !hasResults) {
    renderedPageContent = EmptyStateComponent ? /*#__PURE__*/_jsx(EmptyStateComponent, {}) : /*#__PURE__*/_jsx(Fragment, {
      children: /*#__PURE__*/_jsxs(Section, {
        children: [/*#__PURE__*/_jsx(H4, {
          children: searchValue ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: getI18nKey('noResultsEmptyState.searchTitle_jsx'),
            elements: {
              b: 'span'
            },
            options: {
              searchValue,
              pageHeading: unescapedText(filtersHeadingI18nKey)
            }
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: getI18nKey('noResultsEmptyState.filterTitle'),
            options: {
              pageHeading: unescapedText(filtersHeadingI18nKey)
            }
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: getI18nKey('noResultsEmptyState.message')
          })
        }), activePage > 1 && /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(PageOneLink, {})
        })]
      })
    });
  } else {
    const renderedResultsGrid = isAllCategoriesHomepage ? /*#__PURE__*/_jsxs(Fragment, {
      children: [renderResultsGrid(searchResults.slice(0, 6)), /*#__PURE__*/_jsx(View, {
        marginBlockStart: "1000",
        children: /*#__PURE__*/_jsx(AppCollectionsResultsSection, {
          trackInteraction: trackInteraction
        })
      }), renderResultsGrid(searchResults.slice(6))]
    }) : renderResultsGrid(searchResults);
    const showMaxWarning = activePage >= resultsPageCount && resultsTotal >= SEARCH_RESULTS_MAX;
    const renderedResultsPagination = isSucceeded(requestState) && resultsTotal > resultsLength ? /*#__PURE__*/_jsx(UIPaginator, {
      onPageChange: handlePageChange,
      page: activePage,
      pageCount: resultsPageCount
    }) : null;
    renderedPageContent = /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(Section, {
        ref: resultsRef,
        children: renderedResultsGrid
      }), showMaxWarning && /*#__PURE__*/_jsx(Section, {
        children: /*#__PURE__*/_jsx(ResultsMaxWarning, {})
      }), /*#__PURE__*/_jsx(Section, {
        children: renderedResultsPagination
      })]
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(MediaQueryView, {
      min: ScreenSizes.MD,
      children: /*#__PURE__*/_jsx(StorefrontResultsHeader, {
        title: renderedResultsHeaderTitle,
        description: description,
        renderedResultsFilters: renderedResultsFilters,
        renderedSortDropdown: renderedSortDropdown,
        resultsCount: renderedResultsCount,
        searchTerm: searchValue,
        ungatedMobile: true
      })
    }), /*#__PURE__*/_jsx(MediaQueryView, {
      max: ScreenSizes.MD,
      children: /*#__PURE__*/_jsx(StorefrontResultsHeader, {
        title: renderedResultsHeaderTitle,
        description: description,
        renderedResultsFilters: renderedResultsFilters,
        resultsCount: renderedResultsCount,
        searchTerm: searchValue,
        ungatedMobile: true
      })
    }), renderedPageContent]
  });
};
ResultsPage.propTypes = {
  activePage: PropTypes.number.isRequired,
  description: PropTypes.node,
  emptyStateComponent: PropTypes.func,
  failedComponent: PropTypes.func,
  filterable: PropTypes.bool,
  filters: CustomPropTypes.storefrontFilters.isRequired,
  isAllCategoriesHomepage: PropTypes.bool,
  offset: PropTypes.number,
  onClearFilters: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onResultClick: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  referrer: PropTypes.string,
  requestState: CustomPropTypes.requestState.isRequired,
  resultProps: PropTypes.object,
  resultsPerPage: PropTypes.number,
  searchResult: PropTypes.instanceOf(SearchResult).isRequired,
  sortable: PropTypes.bool,
  title: PropTypes.node,
  trackInteraction: PropTypes.func.isRequired,
  onUpdateFilters: PropTypes.func.isRequired
};
export default ResultsPage;